import React, { useState, useEffect } from 'react';
import { Typography, Box, CircularProgress, Sheet, Alert, Stack } from '@mui/joy';
import Markdown from 'marked-react';
import request from '../helpers/request';
import History from '../components/History';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

const ApiDocsPage = () => {
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDocs = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await request.get('api/v1/docs/');
        setContent(response.data.content || '');
      } catch (e) {
        console.error('Error fetching API docs:', e);
        setError('Failed to load API documentation. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocs();
  }, []);

  return (
    <Box
      component="main"
      className="MainContent"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Stack sx={{ px: { xs: 2, md: 4 }, pt: '0.5%', flexShrink: 0 }}>
        <Box sx={{ mb: 1 }}>
          <History />
        </Box>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 'lg',
              background: (theme) =>
                `linear-gradient(135deg, ${theme.vars.palette.primary[400]} 0%, ${theme.vars.palette.primary[600]} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'sm',
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 28, color: 'white' }} />
          </Box>
          <Typography level="h2" component="h1">API Docs</Typography>
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', px: { xs: 2, md: 4 }, py: 1 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert color="danger" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : (
          <Sheet
            variant="outlined"
            sx={{
              mt: 2,
              p: 3,
              borderRadius: 'sm',
              height: '100%',
              overflow: 'auto',
              '& pre': {
                backgroundColor: 'var(--joy-palette-neutral-100)',
                color: 'var(--joy-palette-neutral-900)',
                padding: '12px',
                borderRadius: '8px',
                overflow: 'auto',
                fontSize: '0.875rem',
                fontFamily: 'monospace',
              },
              '[data-joy-color-scheme="dark"] & pre': {
                backgroundColor: 'var(--joy-palette-neutral-800)',
                color: 'var(--joy-palette-neutral-100)',
              },
              '& code': {
                backgroundColor: 'var(--joy-palette-neutral-100)',
                color: 'var(--joy-palette-neutral-900)',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontFamily: 'monospace',
              },
              '[data-joy-color-scheme="dark"] & code': {
                backgroundColor: 'var(--joy-palette-neutral-800)',
                color: 'var(--joy-palette-neutral-100)',
              },
              '& pre code': {
                padding: 0,
                backgroundColor: 'transparent',
                color: 'inherit',
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                marginTop: '16px',
                marginBottom: '16px',
              },
              '& th, & td': {
                border: '1px solid var(--joy-palette-neutral-300)',
                padding: '8px 12px',
                textAlign: 'left',
              },
              '& th': {
                backgroundColor: 'var(--joy-palette-neutral-100)',
                fontWeight: 600,
              },
              '& h1': {
                fontSize: '1.75rem',
                fontWeight: 700,
                marginTop: '24px',
                marginBottom: '16px',
              },
              '& h2': {
                fontSize: '1.5rem',
                fontWeight: 600,
                marginTop: '20px',
                marginBottom: '12px',
                borderBottom: '1px solid var(--joy-palette-neutral-200)',
                paddingBottom: '8px',
              },
              '& h3': {
                fontSize: '1.25rem',
                fontWeight: 600,
                marginTop: '16px',
                marginBottom: '8px',
              },
              '& p': {
                marginBottom: '12px',
                lineHeight: 1.6,
              },
              '& ul, & ol': {
                paddingLeft: '24px',
                marginBottom: '12px',
              },
              '& li': {
                marginBottom: '4px',
              },
              '& hr': {
                border: 'none',
                borderTop: '2px solid var(--joy-palette-neutral-200)',
                margin: '24px 0',
              },
              '& blockquote': {
                borderLeft: '4px solid var(--joy-palette-primary-300)',
                paddingLeft: '16px',
                marginLeft: 0,
                fontStyle: 'italic',
                color: 'var(--joy-palette-neutral-600)',
              },
              '& a': {
                color: 'var(--joy-palette-primary-500)',
                textDecoration: 'underline',
              },
            }}
          >
            <Markdown>{content}</Markdown>
          </Sheet>
        )}
      </Box>
    </Box>
  );
};

export default ApiDocsPage;
